import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useAppDispatch } from 'hooks/redux';
import { isEqual } from 'lodash';
import { setElementValues } from 'store/rfiSlice';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { answerValueTypes, Element, elementTypes, IElementProps } from '../../types/RfiTypes';

export interface DateTimeElementProps {
  element: Element;
  readOnly?: boolean;
  questionNumber?: number;
  showValue?: boolean;
  designMode?: boolean;
}

const RfiTimePicker = (props: IElementProps) => {
  // Hooks
  const dispatch = useAppDispatch();

  // State
  const [value, setValue] = useState<Dayjs | null>(() => {
    const defaultValue = props.element.answers
      ? props.element.answers[0]?.value ?? ""
      : "";

    if (defaultValue) {
      const date = dayjs(defaultValue);
      return date;
    }
    return null;
  });

  // Handlers
  const handleChange = (event: Dayjs | null) => {
    dispatch(
      setElementValues({
        elementId: props.element.elementId,
        values: [
          {
            value: event?.isValid() ? event?.format("2000-01-01THH:mm:ss") : "",
            answerValueType: answerValueTypes.DEFAULT,
          },
        ],
      })
    );

    setValue(event?.isValid() ? event : null);
  };

  // Render
  if (props.element.elementType === elementTypes.FIELD) {
    if (props.readOnly) {
      return (
        <div className="form-group row">
          <div className="col-lg-3 col-form-label">
            {props.element.fieldLabel}
          </div>
          <div className="col-lg-8 fieldElement">
            {value?.format("MM/DD/YYYY")}
          </div>
          {/* <div className="col-lg-8 fieldElement">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker                            
                readOnly={true}
                value={value}                
              />
            </LocalizationProvider>
            </div> */}
        </div>
      );
    } else
      return (
        <div className="form-group row">
          <div className="col-lg-3 col-form-label">
            <label
              className="col-form-label"
              htmlFor={`timepicker-${props.element.elementId}`}
            >
              {props.element.fieldLabel}{" "}
              {props.overrideRequired === undefined ? (
                props.element.requiredResponse ? (
                  <span className="requiredField">(required)</span>
                ) : (
                  ""
                )
              ) : props.overrideRequired === true ? (
                <span className="requiredField">(required)</span>
              ) : (
                ""
              )}
            </label>
          </div>
          <div className="col-lg-8 fieldElement">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                slotProps={{
                  textField: {
                    id: `timepicker-${props.element.elementId}`,
                  },
                }}
                value={value}
                onChange={handleChange}
              />
            </LocalizationProvider>
          </div>
        </div>
      );
  } else {
    if (props.readOnly) {
      return (
        <>
          <div className="col-form-label">
            {`${props.questionNumber}.`} {props.element.fieldLabel}
          </div>
          {/* <div>{props.showValue ? value?.toString() : ""}</div> */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker readOnly={true} value={value} />
          </LocalizationProvider>
        </>
      );
    } else
      return (
        <>
          <fieldset>
            <legend className="visuallyHidden">
              {props.element.fieldLabel}
            </legend>
            <label
              className="col-form-label"
              htmlFor={`timepicker-${props.element.elementId}`}
            >
              <div>
                {`${props.questionNumber}.`} {props.element.fieldLabel}{" "}
                {props.overrideRequired === undefined ? (
                  props.element.requiredResponse ? (
                    <span className="requiredField">(required)</span>
                  ) : (
                    ""
                  )
                ) : props.overrideRequired === true ? (
                  <span className="requiredField">(required)</span>
                ) : (
                  ""
                )}
              </div>
            </label>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    slotProps={{
                      textField: {
                        id: `timepicker-${props.element.elementId}`,
                      },
                    }}
                    value={value}
                    onChange={handleChange}
                  />
                </LocalizationProvider>
              </LocalizationProvider>
            </div>
          </fieldset>
        </>
      );
  }
};

const areEqual = (prevProps: any, nextProps: any) => {
  return (
    !prevProps.rerender &&
    isEqual(prevProps.element.elementId, nextProps.element.elementId) &&
    isEqual(prevProps.element, nextProps.element) &&
    isEqual(prevProps.designMode, nextProps.designMode) &&
    isEqual(prevProps.readOnly, nextProps.readOnly)
  );
};

const TimePickerElement = React.memo(RfiTimePicker, areEqual);

export default TimePickerElement;
